<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('invoiceConfirm.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('invoiceConfirm.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-space-x-3">
            <ts-button
              outline
              color="info" @click.prevent="goToScan"
              >{{ $t('add_new') }}</ts-button
            >
          </div>
          <div class="d-flex">
            <ts-input
              v-model="search"
              :placeholder="$t('invoiceConfirm.search')"
              style="width: 250px;"
              class="inline-block"
            />
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
        >
          <template v-slot="{ record }">
            <td class="ts-whitespace-no-wrap">
              <p>
                {{ record.branch.branch_name_en }}
              </p>
            </td>
            <td class="ts-whitespace-no-wrap">
              <p
                v-tooltip="$t('invoiceConfirm.mdnNumber')"
                class="ts-font-semibold"
              >
              <i class="fas fa-sticky-note"></i> {{ record.mdo_number }}
              </p>
              <span
                v-tooltip="$t('invoiceConfirm.mdnDate')"
                class="ts-text-orange-500 ts-font-semibold"
              >
                <i class="fas fa-calendar-alt"></i>
                {{ record.mdo_date }}
              </span>
            </td>
            <td class="ts-whitespace-no-wrap">
              <p>
                {{ record.ref_inv_number }}
              </p>
            </td>
            <td class="ts-whitespace-no-wrap">
              <p
                class="ts-font-semibold ts-text-blue-600"
                v-tooltip="$t('invoiceConfirm.studentName')"
              >
                <i class="fas fa-user-graduate"></i>
                {{ record.student_name_en }}
              </p>
              <p v-tooltip="$t('invoiceConfirm.cardNumber')">
                <i class="fas fa-id-card-alt"></i>
                {{ record.card_number }}
              </p>
              <p v-tooltip="$t('invoiceConfirm.sex')">
                <i class="fas fa-transgender-alt"></i>
                {{ record.gender }}
              </p>
            </td>
            <td class="ts-whitespace-no-wrap">
              <p>
                {{ record.ac_year }}
              </p>
            </td>
            <td class="ts-whitespace-no-wrap">
              <p>
                {{ record.term_name }}
              </p>
            </td>
            <td class="ts-whitespace-no-wrap">
              <p>
                {{
                  record.class_name
                }}
              </p>
            </td>
            <td class="ts-whitespace-no-wrap">
              <p>
                {{ record.grade_name_foreign }}
              </p>
            </td>
            <td>
              <p>
                {{ record.comment }}
              </p>
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              <a
                href="#"
                class="text-danger ml-2"
                @click.prevent="pdfView(record)"
              >
                <i class="fas fa-file-pdf"></i>
              </a>
            </td>
          </template> 
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
      <pdf-preview
        v-model="pdf_view"
        :mdo-id="mdoId"
        @cancel="value => (pdf_view = value)"
      />
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState, mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import PdfPreview from './component/pdf_preview'

export default {
  name: 'invoice',
  components: {
    PdfPreview
  },
  data() {
    return {
      visible: false,
      loading: false,
      pdf_view: false,
      mdoId: 0,
      branch_id: [],
      model: {
        void_reason: null
      },
      errors: new Errors(),
      rangeDate: [moment(), moment()]
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('invoiceConfirm', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.invoiceConfirm.search
      },
      set(newValue) {
        this.$store.commit('invoiceConfirm/SET_SEARCH', newValue)
        this.$store.commit('invoiceConfirm/RESET_CURRENT_PAGE')
      }
    },
    is_external() {
      return this.$store.state.authUser.is_external
    },
    columns() {
      return [
        { name: this.$t('invoiceConfirm.branch') },
        { name: this.$t('invoiceConfirm.materialDeliveryNote') },
        { name: this.$t('invoiceConfirm.invoiceReference') },
        { name: this.$t('invoiceConfirm.student') },
        { name: this.$t('invoiceConfirm.academicYear') },
        { name: this.$t('invoiceConfirm.academicTerm') },
        { name: this.$t('invoiceConfirm.class') },
        { name: this.$t('invoiceConfirm.grade') },
        { name: this.$t('invoiceConfirm.comments'), style: 'width: 20%' },
        { name: this.$t('actions'), style: 'text-align:center; width:7%' }
      ]
    }
  },
  methods: {
    ...mapActions('invoiceConfirm', ['destroy']),
    goToScan() {
      this.$router.push({ name: 'invoice-confirm-scanner' })
    },
    fetchData(attributes = {}) {
      this.loading = true
      this.$store
        .dispatch(
          'invoiceConfirm/fetch',
          Object.assign({}, { branch_id: this.branch_id }, attributes)
        )
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    pdfView(record) {
      this.mdoId = record.mdo_id
      this.pdf_view = true
    }
  },
  watch: {
    search: debounce(function() {
      this.fetchData()
    }, 500),
    is_external: debounce(function() {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('invoiceConfirm/RESET_STATE')
    next()
  }
}
</script>
